import { useEffect, useState } from "react";
import { logger } from "@/lib/logger";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Warehouse } from "lucide-react";
import { fmtQty, qtyStep } from "@/lib/constants";

export default function StockAdjustDialog({ item, onClose, onSubmit }) {
  const [mode, setMode] = useState("set");
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (item) {
      setMode("set");
      setValue(String(item.stock_quantity ?? 0));
    }
  }, [item]);

  const submit = async (e) => {
    e.preventDefault();
    const num = Number(value);
    if (Number.isNaN(num)) return;
    setSaving(true);
    try {
      await onSubmit(item, mode === "set" ? { set: Math.max(0, num) } : { add: num });
      onClose();
    } catch (err) {
      logger.error("Stock adjust failed:", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!item} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-sm" data-testid="stock-adjust-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Warehouse className="h-4 w-4 text-blue-600" />
            Adjust Stock — {item?.sku}
          </DialogTitle>
          <DialogDescription className="font-mono-data text-xs">
            {item?.name} · currently {fmtQty(item?.stock_quantity ?? 0, item?.unit)} {item?.unit} on hand
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Adjustment</Label>
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger data-testid="stock-adjust-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="set">Set to (Count)</SelectItem>
                  <SelectItem value="add">Add / Remove (±)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{mode === "set" ? `Quantity (${item?.unit})` : `Delta (${item?.unit})`}</Label>
              <Input
                type="number"
                step={qtyStep(item?.unit)}
                min={mode === "set" ? "0" : undefined}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                required
                autoFocus
                data-testid="stock-adjust-value"
              />
            </div>
          </div>
          <p className="text-[11px] text-slate-400">
            {mode === "add"
              ? "Use a negative number to record consumed or damaged stock. Stock never drops below 0."
              : "Overwrites the on-hand count, e.g. after a physical stock take."}
          </p>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="stock-adjust-submit">
              {saving ? "Saving…" : "Update Stock"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
